/**
 * frontend/ArtistInviteLanding.jsx
 * SoundDAO — Public landing page for artist invite links.
 *
 * Route: /join/artist/:invite_code
 *
 * On mount: stores invite_code in localStorage, fetches nomination data,
 * displays a branded landing page inviting the artist to join as a creator.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authFetch } from './lib/authFetch';
import BrandMark from './components/BrandMark';
import './pages/InviteLanding.css';   // shared invite-* styles (this page had none either)

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function ArtistInviteLanding() {
  const { invite_code } = useParams();
  const navigate = useNavigate();

  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!invite_code) return;

    // Store invite code for post-onboarding completion
    localStorage.setItem('artist_invite_code', invite_code);

    // Fetch invite data
    authFetch(`${API_BASE}/api/nominations/invite/${invite_code}`)
      .then(r => {
        if (!r.ok) throw new Error('Invite not found');
        return r.json();
      })
      .then(data => {
        setInviteData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('[ArtistInviteLanding] fetch error:', err.message);
        setError('This invite link is invalid or has expired.');
        setLoading(false);
      });
  }, [invite_code]);

  const handleJoin = () => {
    // Route to auth with persona pre-set to creator
    navigate('/?persona=creator');
  };

  if (loading) {
    return (
      <div className="invite-screen">
        <div className="invite-card">
          <div className="invite-logo"><BrandMark /></div>
          <div className="app-loading-spinner" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invite-screen">
        <div className="invite-card">
          <div className="invite-logo"><BrandMark /></div>
          <h1 className="invite-title">Invite Not Found</h1>
          <p className="invite-desc">{error}</p>
          <button className="invite-btn" onClick={() => navigate('/')}>
            Go to Big Creative
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-screen">
      <div className="invite-card">
        <div className="invite-logo"><BrandMark /></div>
        <h1 className="invite-title">
          {inviteData.nominator_display_name} wants you on Big Creative
        </h1>
        <p className="invite-desc">
          Join as a creator and start earning SOUND with your fans.
        </p>

        <div className="invite-perks">
          <div className="invite-perk">🎤 <span>Build your fan machine from Raw to Phoenix</span></div>
          <div className="invite-perk">💰 <span>Earn SOUND tokens for every post and fan interaction</span></div>
          <div className="invite-perk">🔥 <span>Schedule Drops and go viral with your street team</span></div>
        </div>

        <button className="invite-btn" onClick={handleJoin}>
          Join as an Artist →
        </button>

        <p className="invite-note">
          No password. No wallet required. Just your email.
        </p>
      </div>
    </div>
  );
}
